import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Calendar, MapPin, Phone, Star, MessageCircle, Briefcase, Languages } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, fmtUSD } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { UserBadges } from '@/components/TrustBadge';
import { EmptyState, StarRating } from '@/components/Primitives';

const initials = (u) => `${(u?.first_name || '?')[0]}${(u?.last_name || '')[0] || ''}`.toUpperCase();

export default function JobeurProfilePage() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/users/${id}`).then((r) => setU(r.data)).catch(() => setU(null));
    api.get('/services', { params: { jobeur_id: id, limit: 100 } }).then((r) => {
      setServices((r.data.items || []).filter((s) => s.jobeur_id === id));
    });
    api.get(`/users/${id}/reviews`).then((r) => setReviews(r.data.reviews || []));
  }, [id]);

  const contact = async () => {
    if (!user) { toast.error('Connectez-vous pour contacter'); return nav('/login'); }
    if (user.id === id) { toast.error("C'est votre propre profil"); return; }
    const r = await api.post('/conversations', { other_user_id: id, initial_message: `Bonjour, je suis intéressé(e) par vos services.` });
    nav(`/messages/${r.data.conversation.id}`);
  };

  if (!u) return <div className="max-w-3xl mx-auto p-6">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="jobeur-profile-page">
      <Card className="p-5 border-[#E2E8F0]">
        <div className="flex flex-col md:flex-row gap-4">
          <Avatar className="h-24 w-24 border-4 border-[#EAF3FF] mx-auto md:mx-0">
            {u.photo_base64 && <AvatarImage src={u.photo_base64} alt="" />}
            <AvatarFallback className="bg-gradient-to-br from-[#1677F2] to-[#0E3F80] text-white text-2xl font-bold">{initials(u)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-[#0B1220]">{u.first_name} {u.last_name}</h1>
              <UserBadges user={u} size="sm" />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-600">
              <span className="flex items-center gap-1"><Star size={14} className="fill-[#F7D618] text-[#F7D618]" /> <span className="font-semibold text-[#0B1220] font-mono-num">{(u.rating_avg || 0).toFixed(1)}</span> ({u.rating_count || 0} avis)</span>
              <span className="flex items-center gap-1"><MapPin size={14} /> {u.commune}, {u.city}</span>
              {u.years_experience > 0 && <span className="flex items-center gap-1"><Briefcase size={14} /> {u.years_experience} ans d&apos;exp.</span>}
              <span className="flex items-center gap-1"><Calendar size={14} /> Rayon {u.travel_radius_km || 10} km</span>
            </div>
            {u.bio && <p className="mt-3 text-sm text-slate-700 leading-relaxed">{u.bio}</p>}
            {u.languages?.length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                <Languages size={14} /> {u.languages.join(', ')}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <Button onClick={contact} className="bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="profile-contact-btn">
                <MessageCircle size={16} className="mr-1" /> Contacter
              </Button>
              <Button variant="outline" className="hidden sm:inline-flex"><Phone size={16} className="mr-1" />Voir le numéro</Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 bg-[#F7FAFF] rounded-xl p-4 border border-[#E2E8F0] min-w-[180px]">
            <p className="text-xs text-slate-600">Jobs réalisés</p>
            <p className="text-3xl font-bold text-[#0E3F80] font-mono-num">{u.jobs_completed || 0}</p>
            <div className="border-t border-[#E2E8F0] mt-1 pt-2">
              <p className="text-xs text-slate-600">Statut</p>
              <p className="text-sm font-semibold text-[#16764A] capitalize">{u.availability_status || 'actif'}</p>
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="services" className="mt-6">
        <TabsList>
          <TabsTrigger value="services" data-testid="profile-tab-services">Services ({services.length})</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="profile-tab-reviews">Avis ({reviews.length})</TabsTrigger>
          <TabsTrigger value="about" data-testid="profile-tab-about">À propos</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-4">
          {services.length === 0 ? (
            <EmptyState title="Aucun service publié" description="Ce jobeur n'a pas encore publié de service." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {services.map((s) => (
                <Link key={s.id} to={`/service/${s.id}`}>
                  <Card className="p-4 hover:shadow-md hover:border-[#C7E2FF]">
                    <h3 className="font-semibold">{s.title}</h3>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{s.description}</p>
                    <p className="mt-2 font-mono-num font-semibold text-[#1677F2]">{fmtUSD(s.price_cents)} {s.price_type === 'hourly' ? '/h' : ''}</p>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="reviews" className="mt-4">
          {reviews.length === 0 ? (
            <EmptyState title="Aucun avis" description="Soyez le premier à laisser un avis après un service réalisé." />
          ) : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <Card key={r.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm">{r.reviewer_name}</p>
                    <StarRating value={r.rating} readonly size={16} />
                  </div>
                  <p className="text-sm text-slate-700 mt-2">{r.comment}</p>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="about" className="mt-4">
          <Card className="p-4">
            <p className="text-sm text-slate-700">{u.bio || 'Aucune présentation renseignée.'}</p>
            {u.skills?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-600 mb-1">Compétences</p>
                <div className="flex flex-wrap gap-1.5">
                  {u.skills.map((s) => <span key={s} className="text-xs bg-[#EAF3FF] text-[#0E3F80] px-2 py-0.5 rounded-full border border-[#C7E2FF]">{s}</span>)}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
